import { shipmentSchema } from "../shared/order-schema";
import { request, write, type VisibleOrder } from "./api";
import { syncQuoteState } from "./orders-view";
import { element, showToast } from "./ui";

export function initializePartnerActions(replaceOrder: (order: VisibleOrder) => void, getOrder: (id: number) => VisibleOrder | undefined) {
  const busy = new Set<number>();
  const downloading = new Set<number>();
  let epoch = 0;
  let cancelTimer: number | undefined;
  let cancelButton: HTMLButtonElement | null = null;

  function resetCancel(): void {
    window.clearTimeout(cancelTimer);
    if (cancelButton) {
      cancelButton.textContent = "取消已转账状态";
      cancelButton.classList.remove("cancel-confirm");
      cancelButton.removeAttribute("aria-label");
      cancelButton = null;
    }
  }

  element("orders").addEventListener("input", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute("data-quote-input")) return;
    const order = getOrder(Number(input.closest<HTMLElement>("[data-id]")?.dataset.id));
    if (order) syncQuoteState(input, order);
  });

  element("orders").addEventListener("submit", async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute("data-quote-form")) return;
    event.preventDefault();
    const input = form.querySelector<HTMLInputElement>("[data-quote-input]")!;
    const order = getOrder(Number(form.closest<HTMLElement>("[data-id]")?.dataset.id));
    if (!order || order.paid_at || busy.has(order.id)) return;
    const body = { quote: input.value };
    const parsed = shipmentSchema.safeParse(body);
    if (!parsed.success) {
      showToast(parsed.error.issues[0].message, true);
      input.focus();
      return;
    }
    const currentEpoch = epoch;
    busy.add(order.id);
    input.disabled = true;
    try {
      const saved = await write<VisibleOrder>(`/orders/${order.id}/shipment`, "PUT", body);
      if (currentEpoch !== epoch) return;
      input.blur();
      replaceOrder(saved);
      showToast("报价已保存");
    } catch (error) {
      if (currentEpoch === epoch) showToast(error instanceof Error ? error.message : "保存失败", true);
    } finally {
      busy.delete(order.id);
      input.disabled = false;
    }
  });

  return {
    reset(): void { epoch += 1; resetCancel(); },
    async download(anchor: HTMLAnchorElement, order: VisibleOrder): Promise<void> {
      const item = order.items.find((item) => String(item.id) === anchor.dataset.download);
      if (!item || downloading.has(item.id)) return;
      const currentEpoch = epoch;
      downloading.add(item.id);
      anchor.setAttribute("aria-busy", "true");
      anchor.textContent = "下载中…";
      try {
        const response = await fetch(anchor.href, { credentials: "same-origin" });
        if (!response.ok) throw new Error("下载失败，请刷新订单后重试");
        const blob = await response.blob();
        if (currentEpoch !== epoch) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = item.file_name;
        document.body.append(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        const saved = await request<VisibleOrder>(`/orders/${order.id}`);
        if (currentEpoch === epoch) replaceOrder(saved);
      } catch (error) {
        if (currentEpoch === epoch) showToast(error instanceof Error ? error.message : "下载失败", true);
      } finally {
        downloading.delete(item.id);
        anchor.removeAttribute("aria-busy");
        if (anchor.isConnected) anchor.textContent = item.downloaded_at ? "✓ 已下载" : "↓ 下载文件";
      }
    },
    async run(button: HTMLButtonElement, order: VisibleOrder): Promise<void> {
      const action = button.dataset.action;
      if (button.disabled || busy.has(order.id)) return;
      if (action !== "paste-tracking" && action !== "receipt" && action !== "address") return;
      if (action === "receipt" && !order.paid_at) {
        const input = button.closest("[data-id]")?.querySelector<HTMLInputElement>("[data-quote-input]");
        const draft = shipmentSchema.safeParse({ quote: input?.value });
        if (!draft.success || draft.data.quote_cents !== order.quote_cents) {
          input?.focus();
          showToast("请先填写报价，按回车保存后再确认收款", true);
          return;
        }
      }
      if (action === "receipt" && order.paid_at && cancelButton !== button) {
        resetCancel();
        cancelButton = button;
        button.textContent = "确认取消";
        button.classList.add("cancel-confirm");
        button.setAttribute("aria-label", "确认取消已转账");
        cancelTimer = window.setTimeout(resetCancel, 5000);
        return;
      }
      resetCancel();
      const currentEpoch = epoch;
      busy.add(order.id);
      button.disabled = true;
      try {
        let saved: VisibleOrder;
        if (action === "address") {
          try { await navigator.clipboard.writeText(order.address); }
          catch { throw new Error("复制失败，请允许网站访问剪贴板后重试"); }
          if (currentEpoch !== epoch) return;
          saved = await write<VisibleOrder>(`/orders/${order.id}/address-copy`, "POST", { address: order.address });
        } else if (action === "paste-tracking") {
          let value: string;
          try { value = await navigator.clipboard.readText(); }
          catch { throw new Error("无法读取剪贴板，请允许网站读取后再点击粘贴"); }
          const body = { tracking_number: value.trim() };
          const parsed = shipmentSchema.safeParse(body);
          if (!parsed.success) throw new Error("剪贴板单号不能为空，且不能超过 100 个字符");
          if (currentEpoch !== epoch) return;
          saved = await write<VisibleOrder>(`/orders/${order.id}/shipment`, "PUT", body);
        } else {
          saved = order.paid_at
            ? await write<VisibleOrder>(`/orders/${order.id}/receipt`, "DELETE", { paid_at: order.paid_at })
            : await write<VisibleOrder>(`/orders/${order.id}/receipt`, "POST", { quote_cents: order.quote_cents });
        }
        if (currentEpoch !== epoch) return;
        replaceOrder(saved);
        showToast(action === "address" ? "地址已复制" : action === "paste-tracking" ? "快递单号已保存" : order.paid_at ? "已取消转账确认" : "已确认转账");
      } catch (error) { if (currentEpoch === epoch) showToast(error instanceof Error ? error.message : "保存失败", true); }
      finally { busy.delete(order.id); button.disabled = false; }
    },
  };
}
